import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useUpdateAppointment } from 'api/appointment/useUpdateAppointment';
import { AxiosInstance } from 'axios';
import {
  addDays,
  addHours,
  format,
  isSameDay,
  min,
  startOfDay,
} from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import difference from 'lodash/difference';
import { parseYMD } from 'utils/dates';
import {
  getCursorStream,
  Stream,
  StreamCursor,
} from 'utils/streams/getCursorStream';
import { useIsMounted } from 'utils/useIsMounted';

import {
  getAvailabilityDates,
  getAvailabilitySlots,
  Slot,
} from '../api/getAvailability';
import { rescheduleAppointment } from '../api/rescheduleAppointment';
import { Appointment, AppointmentStatus } from '../Appointments/types';
import { coerceToDate } from '../dates/coerceToDate';
import { useMergeState } from '../hooks/useMergeState';
import { usePrevious } from '../hooks/usePrevious';
import { CalendarDayViewModel, getCycleDays } from './calendar-utils';

interface CalendarBookingState {
  appointment?: Appointment;
  defaultTimeZone: string;
  selectedTimeZone: string;
  dayViewModels: CalendarDayViewModel[][];
  placeholderViewModels: CalendarDayViewModel[][];
  availableDays?: Set<string>;
  selectedDay: string | null;
  startDate?: Date;
  endDate?: Date;
  isInitialized?: boolean;
  slots?: Slot[] | null;
  finishedLoadingDays: boolean;
  lastLoadedDay: Date | null;
  isConfirming: boolean;
}

interface CalendarBookingActions {
  setSelectedDay: (day: string | null) => Promise<void>;
  confirmAppointment: (slot: Slot) => Promise<Appointment>;
  rescheduleAppointment: (slot: Slot) => Promise<Appointment>;
  changeTimeZone: (tz: string) => void;
}

interface CalendarDerivedState {
  earliestStartTime: Date;
}

const ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.sssXXX";

const formatISO = (date: Date, tz: string, time: string) => {
  const truncated = date.toISOString().split('T')[0];
  const parsedDate = toDate(`${truncated}T${time}`, { timeZone: tz });
  return formatInTimeZone(parsedDate, tz, ISO_FORMAT);
};

const formatISOFromDay = (date: string, tz: string, time: string) => {
  const parsedDate = toDate(`${date}T${time}`, { timeZone: tz });
  return formatInTimeZone(parsedDate, tz, ISO_FORMAT);
};

const DAYS_LOADING_SIZE = 7;

const Context = createContext<
  CalendarBookingState & CalendarBookingActions & CalendarDerivedState
>({
  dayViewModels: [],
  placeholderViewModels: [],
  selectedDay: null,
  setSelectedDay: () => Promise.reject(),
  confirmAppointment: () => Promise.reject(),
  rescheduleAppointment: () => Promise.reject(),
  earliestStartTime: new Date(),
  defaultTimeZone: 'America/Chicago',
  selectedTimeZone: 'America/Chicago',
  changeTimeZone: () => undefined,
  finishedLoadingDays: false,
  lastLoadedDay: null,
  isConfirming: false,
});

export const CalendarProvider: FC<{
  children: ReactNode;
  appointment: Appointment;
  axiosInstance: AxiosInstance;
  blockedDays?: number;
  defaultTimeZone?: string;
  existingAppointments: Appointment[];
}> = ({
  children,
  appointment,
  axiosInstance,
  defaultTimeZone,
  existingAppointments,
}) => {
  ///////////////////
  // Context State //
  ///////////////////
  const usedDefaultTimeZone =
    appointment.timeZone ?? defaultTimeZone ?? 'America/Chicago';

  const { state, mergeState, reset } = useMergeState<CalendarBookingState>({
    appointment,
    dayViewModels: [] as CalendarDayViewModel[][],
    placeholderViewModels: [] as CalendarDayViewModel[][],
    selectedDay: null as unknown as string,
    isInitialized: false,
    defaultTimeZone: usedDefaultTimeZone,
    selectedTimeZone: usedDefaultTimeZone,
    availableDays: new Set(),
    finishedLoadingDays: false,
    lastLoadedDay: null,
    isConfirming: false,
  });

  const { mutateAsync: updateAppointment } = useUpdateAppointment(
    appointment.id,
    axiosInstance,
  );

  // change blocked hours to minimum days out
  // if today is tuesday at 8AM, no booking is allowed until Friday morning
  const earliestStartTime = useMemo(() => {
    let earliestDate = new Date();
    earliestDate = addHours(earliestDate, appointment.minimumWaitHours ?? 48);

    let soonest = earliestDate;

    const scheduleAfterDate = coerceToDate(appointment.scheduleAfterDate);

    if (soonest < scheduleAfterDate) soonest = scheduleAfterDate;

    return startOfDay(soonest);
  }, [appointment]);

  const isMounted = useIsMounted();
  const loadDaysStreamRef =
    useRef<Stream<string[], { startDate: Date; endDate: Date }>>();

  const loadDays = useCallback(
    (_startDate: Date, lastDay: Date, providerId?: string | null) => {
      if (loadDaysStreamRef.current) loadDaysStreamRef.current.cancel();

      loadDaysStreamRef.current = getCursorStream(
        idx => {
          const startDate = addDays(earliestStartTime, idx * DAYS_LOADING_SIZE);
          // todo: this isn't quite right, what if the entire care cycle is only 6 days?
          if (startDate > lastDay) return null;

          const endDate = min([lastDay, addDays(startDate, DAYS_LOADING_SIZE)]);

          return {
            step: idx,
            data: {
              startDate: startDate,
              endDate: endDate,
            },
          };
        },
        async (cursor: StreamCursor<{ startDate: Date; endDate: Date }>) => {
          if (cursor.data.startDate > cursor.data.endDate) return null;
          const startTime = isSameDay(cursor.data.startDate, earliestStartTime)
            ? formatInTimeZone(
                earliestStartTime,
                state.selectedTimeZone,
                ISO_FORMAT,
              )
            : formatISO(cursor.data.startDate, state.selectedTimeZone, '00:00');
          return getAvailabilityDates(axiosInstance, {
            startTime,
            memberId: appointment.memberId,
            endTime: formatISO(
              addDays(cursor.data.endDate, 1),
              state.selectedTimeZone,
              '23:59',
            ),
            appointmentDurationMinutes: appointment.duration,
            appointmentType: appointment.appointmentType,
            providerAccountId: providerId,
            timeZone: state.selectedTimeZone,
            createdAt: appointment.createdAt,
          });
        },
        isMounted,
      );

      return loadDaysStreamRef.current;
    },
    [
      appointment.appointmentType,
      appointment.duration,
      appointment.memberId,
      axiosInstance,
      earliestStartTime,
      isMounted,
      state.selectedTimeZone,
    ],
  );
  const loadSlots = useCallback(
    async (day: string, providerId?: string | null) => {
      const date = parseYMD(day);
      const now = new Date();
      const nowPlusWait = addHours(now, appointment.minimumWaitHours ?? 24);
      const needsFiltering = nowPlusWait > date;

      const startDate = formatISOFromDay(day, state.selectedTimeZone, '00:00');
      const endDate = formatISOFromDay(day, state.selectedTimeZone, '23:59');
      return getAvailabilitySlots(axiosInstance, {
        startTime: startDate,
        endTime: endDate,
        memberId: appointment.memberId,
        appointmentDurationMinutes: appointment.duration,
        appointmentType: appointment.appointmentType,
        providerAccountId: providerId,
        timeZone: state.selectedTimeZone,
      }).then(d => {
        return needsFiltering
          ? d.filter(d => {
              const t = new Date(d.startTime);
              return t > nowPlusWait;
            })
          : d;
      });
    },
    [
      appointment.minimumWaitHours,
      appointment.duration,
      appointment.appointmentType,
      appointment.memberId,
      state.selectedTimeZone,
      axiosInstance,
    ],
  );

  /////////////////////
  // Context Actions //
  /////////////////////
  const setSelectedDay = useCallback(
    (day: string | null): Promise<void> => {
      mergeState({ selectedDay: day, slots: null });

      if (!day) return Promise.resolve();

      const selectedProviderId =
        appointment.appointmentType === 'bcm_evaluation'
          ? null
          : appointment.providerAccountId;

      // trigger some API calls and what not
      return loadSlots(day, selectedProviderId).then(slots => {
        mergeState({ slots });
      });
    },
    [
      appointment.appointmentType,
      appointment.providerAccountId,
      loadSlots,
      mergeState,
    ],
  );

  const changeTimeZone = useCallback(
    (tz: string) => mergeState({ selectedTimeZone: tz, selectedDay: null }),
    [mergeState],
  );

  //////////////////
  // Prop Changes //
  //////////////////
  const prevAppt = usePrevious(appointment);
  const prevTimeZone = usePrevious(state.selectedTimeZone);
  useEffect(() => {
    if (!appointment && appointment === prevAppt) return; // do nothing on first pass

    if (!appointment) {
      reset();
      return;
    }

    if (appointment === prevAppt && state.isInitialized) return;

    // initialize state
    const startDate = coerceToDate(appointment.scheduleAfterDate);
    const endDate = coerceToDate(appointment.scheduleBeforeDate);
    const placeholderViewModels = getCycleDays(startDate, endDate);
    const accountId =
      appointment.appointmentType === 'bcm_evaluation'
        ? null
        : appointment.providerAccountId;

    const availableDays = new Set<string>();

    mergeState({
      selectedDay: null,
      appointment,
      dayViewModels: [],
      placeholderViewModels,
      startDate,
      endDate,
      finishedLoadingDays: false,
      lastLoadedDay: null,
      availableDays,
    });

    loadDays(startDate, endDate, accountId)
      .onData((days, cursor) => {
        // The "hard" block list is to prevent scheduling on a day that member has an existing appointment
        // Future state will likely have a "soft" block list that will need to be tracked separately,
        //   as it will likely involve some warning messaging rather than prohibiting scheduling
        // Casting an array to a Set back to an array has to be inefficient, but hopefully better than pulling in
        // more lodash functions
        const hardBlockList = [
          ...new Set(
            existingAppointments
              .filter(a => !!a.scheduledStartTime && a.status === 'scheduled')
              .map(a => format(new Date(a.scheduledStartTime!), 'yyyy-MM-dd')),
          ),
        ];

        if (appointment.scheduledStartTime) {
          const allowDay = format(
            new Date(appointment.scheduledStartTime!),
            'yyyy-MM-dd',
          );
          const idx = hardBlockList.indexOf(allowDay);
          if (idx > -1) {
            hardBlockList.splice(idx, 1);
          }
        }

        days.forEach(day => availableDays.add(day));

        mergeState({
          isInitialized: true,
          availableDays: new Set(
            difference([...availableDays, ...days], hardBlockList),
          ),
          lastLoadedDay: cursor.data.endDate,
          dayViewModels: getCycleDays(startDate, cursor.data.endDate),
        });
      })
      .onComplete(() => mergeState({ finishedLoadingDays: true }))
      .start();

    // explicitely ignoring state.availableDays - we may need a better test here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointment,
    existingAppointments,
    loadDays,
    mergeState,
    prevAppt,
    reset,
    state.isInitialized,
  ]);

  // triggers for time zone change
  useEffect(() => {
    if (!appointment) return;
    if (!prevTimeZone) return;

    if (prevTimeZone === state.selectedTimeZone) return;

    const startDate = coerceToDate(appointment.scheduleAfterDate);
    const endDate = coerceToDate(appointment.scheduleBeforeDate);
    const accountId =
      appointment.appointmentType === 'bcm_evaluation'
        ? null
        : appointment.providerAccountId;

    mergeState({
      finishedLoadingDays: false,
      lastLoadedDay: null,
      dayViewModels: [],
    });

    loadDays(startDate, endDate, accountId)
      .onData((days, cursor) => {
        days.forEach(day => state.availableDays?.add(day));

        mergeState({
          isInitialized: true,
          availableDays: new Set([...(state.availableDays ?? []), ...days]),
          lastLoadedDay: cursor.data.endDate,
          dayViewModels: getCycleDays(startDate, cursor.data.endDate),
        });
      })
      .onComplete(() => mergeState({ finishedLoadingDays: true }))
      .start();
    // explicitely ignoring state.availableDays - we may need a better test here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedTimeZone, appointment, prevTimeZone, loadDays, mergeState]);

  const context = {
    ...state,
    earliestStartTime,
    setSelectedDay,
    confirmAppointment: async (slot: Slot, providerAccountId?: string) => {
      const randomIndex = Math.floor(Math.random() * slot.accountIds.length);

      mergeState({ isConfirming: true });
      try {
        const response = await updateAppointment({
          providerAccountId: providerAccountId
            ? providerAccountId
            : slot.accountIds[randomIndex],
          scheduledStartTime: slot.startTime,
          timeZone: state.selectedTimeZone,
          status: AppointmentStatus.Scheduled,
        });

        return response;
      } finally {
        mergeState({ isConfirming: false });
      }
    },
    rescheduleAppointment: (slot: Slot, providerAccountId?: string) => {
      const randomIndex = Math.floor(Math.random() * slot.accountIds.length);

      return rescheduleAppointment(axiosInstance, appointment.id, {
        providerAccountId: providerAccountId
          ? providerAccountId
          : slot.accountIds[randomIndex],
        scheduledStartTime: slot.startTime,
        timeZone: state.selectedTimeZone,
        status: AppointmentStatus.Scheduled,
      });
    },
    changeTimeZone,
  };

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const useCalendarContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useCalendarContext must be in a CalendarProvider');
  }

  return context;
};
